import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import SmartImage from "@/components/SmartImage";
import { ArrowLeft, BookOpen } from "lucide-react";

export default function Author() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/authors/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      });
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl">Author not found.</h1>
        <Link to="/browse" className="btn-primary mt-8 inline-flex">← Back to shelf</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted_ink">Loading…</div>;
  }

  return (
    <div data-testid="author-page" className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20">
      <Link to="/browse" className="label-eyebrow text-muted_ink hover:text-terracotta inline-flex items-center gap-2">
        <ArrowLeft className="w-3 h-3" /> Back to shelf
      </Link>

      <div className="mt-6 border-b border-edge pb-10">
        <div className="label-eyebrow text-sage">Author</div>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-2" data-testid="author-name">
          {data.name}
        </h1>
        {data.bio ? (
          <p className="text-muted_ink text-lg leading-relaxed mt-6 max-w-2xl italic">
            {data.bio}
          </p>
        ) : (
          <p className="text-muted_ink mt-6 max-w-2xl italic">
            {data.name} has {data.book_count} book{data.book_count === 1 ? "" : "s"} on Folio.
          </p>
        )}
      </div>

      <div className="mt-12">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Their shelf</div>
            <h2 className="font-serif text-3xl mt-2">{data.book_count} book{data.book_count === 1 ? "" : "s"}</h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {data.books.map((b) => {
            const cover = b.cover_url || (b.cover_path ? `${API}/files/${b.cover_path}` : null);
            return (
              <Link
                key={b.id}
                to={`/book/${b.id}`}
                data-testid={`author-book-${b.id}`}
                className="group block"
              >
                <div className="aspect-[3/4] overflow-hidden bg-linen book-card-cover">
                  {cover && <SmartImage src={cover} alt={b.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />}
                </div>
                <div className="mt-3">
                  <div className="label-eyebrow text-sage text-[10px]">{b.category}</div>
                  <div className="font-serif text-lg mt-1 leading-tight group-hover:text-terracotta transition-colors">
                    {b.title}
                  </div>
                  <div className="text-sm mt-1">${Number(b.price).toFixed(2)}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
